


import {
    Typography,
    Box,
    Collapse,
} from "@mui/material";


import React, { useState, useEffect } from "react";
import { useTypewriter } from "../../hooks/useTypewriter";

type RevealSectionProps = {
    title: string;
    start: boolean;
    forceShown?: boolean;
    onDone: () => void;
    children: React.ReactNode;
    scrollIntoView?: () => void;
    titleSpeed?: number;
    itemDelay?: number;
    collapseTimeout?: number;
};

const wait = (ms: number) => new Promise(r => setTimeout(r, ms));

const RevealSection = ({
    title,
    start,
    forceShown = false,
    onDone,
    children,
    scrollIntoView,
    titleSpeed = 20,
    itemDelay = 150,
    collapseTimeout = 300,
}: RevealSectionProps) => {
    const [stage, setStage] = useState<"idle" | "typing" | "releasing" | "done">("idle");
    const [visibleIdx, setVisibleIdx] = useState(-1);
    const tw = useTypewriter(title, titleSpeed, [start]); // 👈 usa titleSpeed

    useEffect(() => {
        if (forceShown && stage !== "done") {
            setStage("done");
            setVisibleIdx(React.Children.count(children) - 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forceShown, children]);

    useEffect(() => {
        if (!start || forceShown || stage === "done") return;
        setStage("typing");
        scrollIntoView?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [start, forceShown]);

    useEffect(() => {
        if (forceShown || stage !== "typing" || !tw.done) return;
        setStage("releasing");
        (async () => {
            const kids = React.Children.toArray(children);
            for (let i = 0; i < kids.length; i++) {
                setVisibleIdx(i);
                await wait(itemDelay); // 👈 usa itemDelay
            }
            setStage("done");
            onDone();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stage, tw.done, forceShown, children, itemDelay]);

    const kids = React.Children.toArray(children);
    const showAll = forceShown || stage === "done";

    return (
        <Box>
            <Typography sx={{ fontWeight: 700, mb: 1.25 }}>
                {showAll ? title : start ? tw.out : ""}
                {!showAll && start && !tw.done ? <span style={{ opacity: .6 }}>▌</span> : null}
            </Typography>

            <Box sx={{ display: "grid", gap: 1 }}>
                {kids.map((k, i) => {
                    const shouldShow = showAll || (start && i <= visibleIdx);
                    return (
                        <Collapse
                            key={i}
                            in={shouldShow}
                            timeout={collapseTimeout}  // 👈 usa collapseTimeout
                            mountOnEnter
                        >
                            <Box sx={{ opacity: shouldShow ? 1 : 0, transition: `opacity ${collapseTimeout}ms` }}>
                                {k}
                            </Box>
                        </Collapse>
                    );
                })}
            </Box>
        </Box>
    );
};

export default RevealSection;
